import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, RefreshCw, ArrowRight, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTABand from "@/components/CTABand";
import { DISCLAIMER } from "@/lib/constants";
import api from "@/lib/api";

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setA(null); setNotFound(false);
    api.get(`/public/articles/${slug}`).then((r) => setA(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="container-x py-24 text-center">
      <h1 className="font-serif text-3xl font-bold text-navy">Article not found</h1>
      <Link to="/insights" className="btn-gold mt-6">Back to Insights</Link>
    </div>
  );
  if (!a) return <div className="py-32 text-center text-slate-400">Loading…</div>;

  return (
    <>
      <Seo title={a.seo_title || a.title} description={a.seo_desc || a.excerpt} />
      <section className="relative overflow-hidden bg-navy">
        <img src={a.image} alt="" aria-hidden="true" style={{ opacity: 0.28 }} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,25,44,0.86) 0%, rgba(11,25,44,0.92) 55%, rgba(11,25,44,0.98) 100%)" }} />
        <div className="container-x relative py-14">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Insights", to: "/insights" }, { label: a.category }]} />
          <span className="eyebrow mt-5 block">{a.category}</span>
          <h1 className="mt-3 max-w-4xl font-serif text-4xl font-bold leading-tight text-white sm:text-5xl">{a.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-slate-200">
            <span className="flex items-center gap-1.5"><Calendar size={14} /> Published {fmt(a.published_at)}</span>
            <span className="flex items-center gap-1.5"><RefreshCw size={14} /> Last reviewed {fmt(a.updated_at)}</span>
            {a.read_minutes && <span className="flex items-center gap-1.5"><Clock size={14} /> {a.read_minutes} min read</span>}
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-3">
          <article className="prose-apex lg:col-span-2" dangerouslySetInnerHTML={{ __html: a.body }} />
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {a.related_service_item && (
                <div className="rounded-xl border border-slate-200 bg-cream p-6">
                  <span className="eyebrow">Related Service</span>
                  <h3 className="mt-2 font-serif text-lg font-semibold text-navy">{a.related_service_item.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{a.related_service_item.short_desc}</p>
                  <Link to={`/services/${a.related_service_item.slug}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
                    View service <ArrowRight size={14} />
                  </Link>
                </div>
              )}
              <div className="rounded-xl bg-navy p-6">
                <h3 className="font-serif text-lg font-semibold text-white">Speak to ApexPath</h3>
                <p className="mt-2 text-sm text-slate-300">Every situation is different. Get advice tailored to your circumstances.</p>
                <Link to="/book-consultation" className="btn-gold mt-4 w-full text-sm">Book a Consultation <ArrowRight size={14} /></Link>
              </div>
            </div>
          </aside>
        </div>
        <div className="container-x mt-10">
          <p className="rounded-lg border-l-4 border-gold bg-cream p-4 text-xs italic text-slate-500">{a.disclaimer || DISCLAIMER}</p>
        </div>
      </section>

      <CTABand />
    </>
  );
}
